import { Flex, Row, Typography } from "antd";
import { LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { EmptyState } from "@libertify/ui";
import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { NotificationStatus } from "store/websocketStore";
import { useProjectDetails } from "./hooks/useProjectDetails";
import { useAssetById } from "hooks/useAssetById";
import type {
  ContactResponse,
  MediaGenerationSettingsResponse,
  PresenterProfileResponse,
  SlidesAsset,
  StoryboardAsset,
} from "pages/media/types";
import { useStoryboardWithSlides } from "hooks/useStoryboardWithSlides/";
import mainBg from "../../../assets/main-background.png";
import { useCustom, useGo, useOne, useTranslate } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { useWsSubscription } from "hooks/useWsSubscription/";
import { NOTIFICATIONS_EVENT_TYPE } from "store/websocketStore/";
import { useScriptSpecs } from "hooks/useScriptSpecs";
import { useMediaGenerationStatus } from "./hooks/useMediaGenerationStatus";
import { useBrandKit } from "hooks/useBrandKit/";
import { useStreamingTemplates } from "./components/slides/hooks/useStreamingTemplates";
import { useBoundStore } from "@/store";
import { Autopilot } from "./components/Autopilot/Autopilot";
import type { DocumentInsightAsset } from "./components/Autopilot/types";

export const SlidesStreaming = () => {
  const t = useTranslate();

  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );

  const { id: projectId } = useParams<{ id: string }>();
  const [scriptSpecsAssetId, setScriptSpecsAssetId] = useState<
    string | undefined
  >(undefined);
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isPublished, setIsPublished] = useState(false);

  // ─── Prompt project insight tracking ─────────────────────────────────────────
  const [insightAssetId, setInsightAssetId] = useState<string | undefined>(
    undefined
  );
  const insightStatusesRef = useRef<Set<string>>(new Set());

  const go = useGo();

  const {
    data: projectData,
    isLoading: isLoadingProject,
    refetch: refetchProject,
  } = useProjectDetails({ projectId });

  // Ref-based visibility refetch so the handler always calls the latest functions
  // (avoids stale closure — refetchAll/refetchVoiceoverAll are declared after this effect)
  const visibilityRefetchRef = useRef(() => {});

  useEffect(() => {
    setSidebarCollapsed(true);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        visibilityRefetchRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      setSidebarCollapsed(false);
      clearTimeout(refetchTimeoutRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storyboard = projectData?.data.storyboards[0];
  const isPromptProject =
    projectData?.data.documents?.[0]?.retrieval?.provider?.type ===
    "DocumentGeneratorProvider";

  // Pick up insight_asset_id from project data whenever it appears.
  // Works for page refresh (already set) and live flow (set after WS-triggered refetches).
  const projectInsightAssetId =
    projectData?.data.documents?.[0]?.insight_asset_id;
  if (isPromptProject && !insightAssetId && projectInsightAssetId) {
    setInsightAssetId(projectInsightAssetId);
  }

  // DocumentInsightSaved WS events may not include project_id, so the project
  // subscription silently drops them. Poll the project until we have the insight.
  useEffect(() => {
    if (!isPromptProject || insightAssetId) return;
    const interval = setInterval(() => {
      void refetchProject();
    }, 5000);
    return () => clearInterval(interval);
  }, [isPromptProject, insightAssetId, refetchProject]);

  // Detect if slides were already generated when the page loaded (refresh scenario).
  // Uses a ref so the value is available synchronously on the same render that
  // projectData first appears — avoiding a one-frame delay where Autopilot would
  // mount with skipAnimations=false before the state update triggers a re-render.
  const hadSlidesOnLoadRef = useRef<boolean | null>(null);
  if (hadSlidesOnLoadRef.current === null && projectData) {
    hadSlidesOnLoadRef.current = !!storyboard?.slides_asset_id;
  }
  const hadSlidesOnLoad = hadSlidesOnLoadRef.current;

  // Use WebSocket-provided ID if available, otherwise fall back to project data
  const effectiveScriptSpecsAssetId =
    scriptSpecsAssetId ?? storyboard?.script_specs_asset_id;

  const { data: storyBoardAsset } = useAssetById<StoryboardAsset>({
    assetId: storyboard?.asset_id,
  });

  const { data: slideAsset } = useAssetById<SlidesAsset>({
    assetId: storyboard?.slides_asset_id,
  });

  const { data: scriptSpecsAsset } = useAssetById({
    assetId: effectiveScriptSpecsAssetId,
  });

  const { thinkingProcess } = useScriptSpecs(scriptSpecsAsset?.data.path);

  // Fetch the document insight asset metadata, then its content
  const { data: insightAsset } = useAssetById({
    assetId: insightAssetId,
  });

  const insightPath = insightAsset?.data.path;
  const { data: documentInsight } = useQuery({
    queryKey: ["documentInsight", insightPath] as const,
    queryFn: async (): Promise<DocumentInsightAsset> => {
      const response = await fetch(insightPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch document insight: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!insightPath,
    staleTime: 1000 * 60 * 5,
    // The asset file is updated incrementally (reflection first, then outline).
    // Keep refetching until the full payload is available.
    refetchInterval: (data) => (data?.outline ? false : 3000),
  });

  // Keep last valid paths to prevent data gaps during asset transitions.
  // When slides_asset_id changes (new asset), useAssetById briefly returns undefined
  // while fetching the new asset. Without this, sections collapses to [] and scroll jumps.
  const storyboardPath = storyBoardAsset?.data.path;
  const slidesPath = slideAsset?.data.path;
  const lastStoryboardPathRef = useRef<string | undefined>();
  const lastSlidesPathRef = useRef<string | undefined>();
  if (storyboardPath) lastStoryboardPathRef.current = storyboardPath;
  if (slidesPath) lastSlidesPathRef.current = slidesPath;

  const { sections, refetchAll } = useStoryboardWithSlides(
    lastStoryboardPathRef.current,
    lastSlidesPathRef.current
  );
  const voiceoverSections = sections;
  const refetchVoiceoverAll = refetchAll;

  const brandKitData = useBrandKit({
    brandKitId: projectData?.data.settings.brand_kit_id,
    organizationId: projectData?.data.organization_id,
  });
  const { refetchBrandKit } = brandKitData;

  const { data: mediaSettingsData, refetch: refetchMediaSettings } =
    useCustom<MediaGenerationSettingsResponse>({
      url: `media/${projectData?.data.organization_id}/projects/${projectId}/media/default/settings`,
      method: "get",
      queryOptions: {
        enabled: !!projectId && !!projectData?.data.organization_id,
        queryKey: ["ProjectMediaSettings", projectId],
      },
      errorNotification: false,
    });

  // Keep the visibility refetch handler up-to-date with the latest refetch functions
  useEffect(() => {
    visibilityRefetchRef.current = () => {
      void refetchProject();
      void refetchAll();
      void refetchVoiceoverAll();
      void refetchMediaSettings();
      void refetchBrandKit();
    };
  }, [
    refetchProject,
    refetchAll,
    refetchVoiceoverAll,
    refetchMediaSettings,
    refetchBrandKit,
  ]);

  // Fetch contact to get presenter_id
  const { data: contactData } = useOne<ContactResponse>({
    resource: `media/${projectData?.data.organization_id}/contacts`,
    id: projectData?.data.settings.contact_id ?? undefined,
    queryOptions: {
      enabled:
        !!projectData?.data.settings.contact_id &&
        !!projectData.data.organization_id,
    },
  });

  // Fetch presenter profile
  const { data: presenterData, isLoading: isPresenterLoading } =
    useOne<PresenterProfileResponse>({
      resource: `media/${projectData?.data.organization_id}/presenter_profiles`,
      id: contactData?.data.presenter_id,
      queryOptions: {
        enabled:
          !!contactData?.data.presenter_id &&
          !!projectData?.data.organization_id,
      },
    });

  // Flatten slides from all sections for template mapping
  const slides = useMemo(() => {
    return sections.flatMap((section) => section.slides);
  }, [sections]);

  // Use the streaming templates hook for progressive template loading
  const { templates, mediaAspectRatio } = useStreamingTemplates({
    organizationId: projectData?.data.organization_id,
    mediaSettings: mediaSettingsData?.data,
    brandKitData,
    slides,
    presenter: presenterData?.data,
    isPresenterLoading,
  });

  const { isError: isGenerationError, isStoryboardError } =
    useMediaGenerationStatus(projectData?.data);

  const [storyboardTimedOut, setStoryboardTimedOut] = useState(false);
  const storyboardErrorCountRef = useRef(0);
  const storyboardTimerStartRef = useRef<number | null>(null);
  const STORYBOARD_TIMEOUT_MS = 3 * 60 * 1000;
  useEffect(() => {
    if (!isStoryboardError) {
      // Only reset if this is the first occurrence (transient recovery)
      if (storyboardErrorCountRef.current <= 1) {
        setStoryboardTimedOut(false);
      }
      storyboardTimerStartRef.current = null;
      return;
    }
    storyboardErrorCountRef.current += 1;
    // Second+ occurrence: show error immediately, no grace period
    if (storyboardErrorCountRef.current > 1) {
      setStoryboardTimedOut(true);
      return;
    }
    if (thinkingProcess.length > 0) return;
    storyboardTimerStartRef.current = Date.now();
    const timer = setTimeout(
      () => setStoryboardTimedOut(true),
      STORYBOARD_TIMEOUT_MS
    );

    // Catch up if the tab was backgrounded and the timeout was throttled
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        storyboardTimerStartRef.current !== null &&
        Date.now() - storyboardTimerStartRef.current >= STORYBOARD_TIMEOUT_MS
      ) {
        setStoryboardTimedOut(true);
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isStoryboardError, thinkingProcess.length]);

  const showError = isGenerationError || storyboardTimedOut;

  useWsSubscription(
    { type: "project" as const, id: projectId },
    (notification) => {
      console.info("Received WebSocket notification:", notification);
      switch (notification.event) {
        case NOTIFICATIONS_EVENT_TYPE.ProjectStatusChanged:
          if (notification.status === NotificationStatus.SlidesGenerated) {
            void refetchAll();
            void refetchVoiceoverAll();
          }
          if (notification.status === NotificationStatus.Published) {
            setIsPublished(true);
          }
          void refetchProject();
          void refetchMediaSettings();
          break;
        case NOTIFICATIONS_EVENT_TYPE.DocumentStatusChanged:
          if (notification.status === NotificationStatus.ContentExtracted) {
            refetchProject().catch((error) => {
              console.error(
                "Error refetching project after DocumentStatusChanged notification:",
                error
              );
            });
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.SlideStatusChanged:
          if (notification.status === NotificationStatus.Ready) {
            // Debounce: multiple sections may become Ready in rapid succession.
            // Collapse into a single refetch cycle to avoid concurrent fetches.
            clearTimeout(refetchTimeoutRef.current);
            refetchTimeoutRef.current = setTimeout(() => {
              refetchProject()
                .then(() => {
                  void refetchAll();
                  void refetchVoiceoverAll();
                })
                .catch((error) => {
                  console.error(
                    "Error refetching project after SlideStatusChanged notification:",
                    error
                  );
                });
              void refetchMediaSettings();
            }, 300);
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.ScriptSpecsSaved:
          if (notification.asset_id) {
            setScriptSpecsAssetId(notification.asset_id);
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.BrandkitStatusChanged:
          if (notification.status === NotificationStatus.Generated) {
            void refetchBrandKit();
          }
          break;
        case NOTIFICATIONS_EVENT_TYPE.DocumentInsightSaved:
          if (notification.asset_id) {
            insightStatusesRef.current.add(notification.status);
            // Wait for both reflection and outline events before fetching
            if (
              insightStatusesRef.current.has("reflection") &&
              insightStatusesRef.current.has("outline")
            ) {
              setInsightAssetId(notification.asset_id);
            }
            // Always refetch project so the render-time fallback can pick up
            // insight_asset_id even if one of the two WS events was missed.
            void refetchProject();
          }
          break;
      }
    }
  );

  if (!projectId) {
    console.error("Project ID is undefined");
    go({ to: "/media/projects" });
    return;
  }

  if (isLoadingProject && !projectData) {
    return (
      <Row justify="center" style={{ marginTop: 100 }}>
        <Flex vertical gap="middle" align="center">
          <LoadingOutlined style={{ fontSize: 48 }} spin />
          <Typography.Text>
            {t("media.autopilot.creatingExperience")}
          </Typography.Text>
        </Flex>
      </Row>
    );
  }

  return (
    <Flex
      style={{
        height: "100vh",
        margin: -24,
        backgroundImage: `url(${mainBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      tabIndex={0}
    >
      {showError ? (
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ width: "100%", height: "100%" }}
        >
          <EmptyState
            icon={<WarningOutlined style={{ color: "#ff4d4f" }} />}
            title={t("media.autopilot.error.title")}
            description={t("media.autopilot.error.description")}
            action={{
              label: t("media.autopilot.error.tryAgain"),
              onClick: () => go({ to: "/media/projects/create" }),
            }}
          />
        </Flex>
      ) : (
        <Autopilot
          projectId={projectId}
          objectives={projectData.data.storyboards[0].objectives}
          sections={sections}
          voiceoverSections={voiceoverSections}
          thinkingProcess={thinkingProcess}
          templates={templates}
          mediaAspectRatio={mediaAspectRatio}
          brandKitData={brandKitData}
          isPublished={isPublished}
          isStoryboardError={isStoryboardError}
          isPromptProject={isPromptProject}
          documentInsight={documentInsight}
          skipAnimations={hadSlidesOnLoad === true}
        />
      )}
    </Flex>
  );
};
